import { create } from "zustand";

import type { CommentID, ModelCommentV2 } from "../hooks/useComments";
import { useTracker } from "../hooks/useTracker";
import { useViewLinkId, useWebExport } from "../hooks/useWebExport";
import type TrackingAdapter from "../lib/TrackingAdapter";

type ToolChangeSource = "tool_menu" | "keyboard_shortcut" | "mouse_click" | null;
type InternalTelemetryOptions = {
  tracker: InstanceType<typeof TrackingAdapter>;
  viewLinkId: string;
  webExportId: string | undefined;
};

const defaultNewCommentPosition: ModelCommentV2 = {
  commentPosition: { x: 0, y: 0, z: 0 },
  commentNormal: { x: 0, y: 0, z: 0 },
  cameraTarget: { x: 0, y: 0, z: 0 },
  cameraOrbit: { phi: 0, theta: 0, radius: 0 },
  fieldOfView: 0,
};

interface CommentsState {
  isInAddCommentMode: boolean;
  openCommentId: string | undefined;
  isNewCommentOpen: boolean;
  newCommentPosition: ModelCommentV2;
}

interface CommentsActions {
  setIsInAddCommentMode: (
    isInCommentMode: boolean,
    options: { source: ToolChangeSource } & InternalTelemetryOptions
  ) => void;
  toggleIsInAddCommentMode: (
    options: { source: ToolChangeSource } & InternalTelemetryOptions
  ) => void;
  setOpenCommentId: (commentId: CommentID | undefined) => void;
  setIsNewCommentOpen: (isNewCommentOpen: boolean) => void;
  newCommentPosition: ModelCommentV2;
  setNewCommentPosition: (newCommentPosition: ModelCommentV2) => void;
  reset: () => void;
}

const initialState: CommentsState = {
  isInAddCommentMode: false,
  openCommentId: undefined,
  isNewCommentOpen: false,
  newCommentPosition: defaultNewCommentPosition,
};

const useCommentsStoreInternal = create<CommentsState & CommentsActions>((set) => {
  return {
    ...initialState,
    setIsInAddCommentMode: (isInAddCommentMode, { source, tracker, viewLinkId, webExportId }) => {
      if (source) {
        tracker.trackActiveToolChanged({
          initiatedFrom: source,
          newTool: isInAddCommentMode ? "commenting" : "camera_control",
          viewLinkId,
          webExportId,
        });
      }
      set({ isInAddCommentMode });
    },
    toggleIsInAddCommentMode: ({ source, tracker, viewLinkId, webExportId }) => {
      set((prev) => {
        const newVal = !prev.isInAddCommentMode;
        if (source) {
          tracker.trackActiveToolChanged({
            initiatedFrom: source,
            newTool: newVal ? "commenting" : "camera_control",
            viewLinkId,
            webExportId,
          });
        }
        return { isInAddCommentMode: newVal };
      });
    },
    setOpenCommentId: (openCommentId: CommentID | undefined) => set({ openCommentId }),
    setIsNewCommentOpen: (isNewCommentOpen: boolean) => set({ isNewCommentOpen }),
    setNewCommentPosition: (newCommentPosition: ModelCommentV2) => set({ newCommentPosition }),
    reset: () => set(initialState),
  };
});

export const useCommentsStore = () => {
  const tracker = useTracker();
  const viewLinkId = useViewLinkId() ?? "";
  const { data: webExportData } = useWebExport();
  const commentStore = useCommentsStoreInternal();

  const setIsInAddCommentMode = (isInCommentMode: boolean, options: { source: ToolChangeSource }) =>
    commentStore.setIsInAddCommentMode(isInCommentMode, {
      ...options,
      tracker,
      viewLinkId,
      webExportId: webExportData?.webExport.webExportId,
    });
  const toggleIsInAddCommentMode = (options: { source: ToolChangeSource }) =>
    commentStore.toggleIsInAddCommentMode({
      ...options,
      tracker,
      viewLinkId,
      webExportId: webExportData?.webExport.webExportId,
    });

  return { ...commentStore, setIsInAddCommentMode, toggleIsInAddCommentMode };
};
